import { Trans } from "@lingui/react/macro";
import { cn } from "../lib/cn";
import { Button } from "./button";
import { formatCount } from "../locale";

// A list that is not whole always says so, and offers the rest: Previous/Next under a back-office table, "Show N more"
// under a store-side list. Numbers in tabular figures; a hairline above; no page numbers to jump to — search does that job.

const count = formatCount;

export type PagerProps = {
  /** Ordinal of the first row on this page (1 on the first page). */
  from: number;
  /** Rows on this page. */
  shown: number;
  total?: number | null;
  hasPrevious: boolean;
  hasNext: boolean;
  onPrevious: () => void;
  onNext: () => void;
  loading?: boolean;
  className?: string;
};

/** Previous / Next under a table, with where you are: "51–100 of 1,234". Renders nothing when the list is one page. */
export function Pager({ from, shown, total, hasPrevious, hasNext, onPrevious, onNext, loading = false, className }: PagerProps) {
  if (!hasPrevious && !hasNext) return null;
  const first = count(from);
  const last = count(from + shown - 1);
  const all = total != null ? count(total) : null;
  return (
    <div className={cn("flex items-center justify-between gap-4 border-t border-line pt-3", className)}>
      <span className="text-xs tabular-nums text-ink-3">
        {shown === 0 ? <Trans>No rows</Trans> : all == null ? `${first}–${last}` : <Trans>{first}–{last} of {all}</Trans>}
      </span>
      <div className="flex items-center gap-2">
        <Button size="sm" onClick={onPrevious} disabled={!hasPrevious || loading}>
          <Trans>Previous</Trans>
        </Button>
        <Button size="sm" onClick={onNext} disabled={!hasNext || loading}>
          <Trans>Next</Trans>
        </Button>
      </div>
    </div>
  );
}

export type LoadMoreProps = {
  shown: number;
  total?: number | null;
  /** How many the next click adds; names the button. */
  step: number;
  hasMore: boolean;
  onMore: () => void;
  loading?: boolean;
  className?: string;
};

/**
 * "Showing 20 of 134" and one full-width button for the next batch, under a list that stays a single scroll. The button is
 * a 44px target on a phone (in pixels: on the 14px root `h-11` would be 38.5px) and a regular one from `md`.
 */
export function LoadMore({ shown, total, step, hasMore, onMore, loading = false, className }: LoadMoreProps) {
  if (!hasMore) return null;
  const showing = count(shown);
  const all = total != null ? count(total) : null;
  return (
    <div className={cn("flex flex-col gap-2 border-t border-line pt-3", className)}>
      <span className="text-2xs font-medium uppercase tracking-wider text-ink-3">
        {all == null ? <Trans>Showing {showing}</Trans> : <Trans>Showing {showing} of {all}</Trans>}
      </span>
      <Button className="h-[44px] w-full md:h-9" onClick={onMore} disabled={loading}>
        {loading ? <Trans>Loading…</Trans> : <Trans>Show {step} more</Trans>}
      </Button>
    </div>
  );
}

/** The quiet note a capped or searched list carries at its top right. Renders nothing when every row is on screen. */
export function ShowingCount({ shown, total, className }: { shown: number; total?: number | null; className?: string }) {
  if (total == null || shown >= total) return null;
  const showing = count(shown);
  const all = count(total);
  return (
    <span className={cn("text-2xs font-medium uppercase tracking-wider text-ink-3", className)}>
      <Trans>Showing {showing} of {all}</Trans>
    </span>
  );
}
